import { konfliktBereitsErfuellt } from '@asas/domain/konflikt';
import type { OptimisticLockConflict, Versioned } from '@asas/domain/konflikt';

import { trpcKonflikt } from '../trpc';

import type { Entwurfsspeicher } from './entwurf';
import { klassifiziereFehler } from './fehler';
import type { FehlerBefund } from './fehler';
import { geteilterSerialisierer } from './serialisierung';
import type { Serialisierer } from './serialisierung';

/**
 * Schreibwarteschlange für versionierte Werte (AC-409, AK 4).
 *
 * Bricht während der Eingabe die Verbindung ab, bleibt der Wert erhalten und
 * geht bei Wiederverbindung erneut ab — mit DERSELBEN Version, die beim Tippen
 * galt. Der Server entscheidet dann wie beim ersten Versuch: Ist die Version
 * inzwischen weitergelaufen, wird nichts überschrieben, sondern der Konflikt
 * mit beiden Werten an die aufrufende Komponente gemeldet. Den Einbau in eine
 * Zelle macht AC-403.
 *
 * WARUM HIER EINE SELBSTTÄTIGE WIEDERHOLUNG ERLAUBT IST, obwohl `fehler.ts`
 * sie sonst ausschließt: Sie ist nur gefahrlos, weil jeder Auftrag eine
 * Version trägt (`Versioned`). Hat der erste Versuch trotz Netzfehler gewirkt,
 * trifft der zweite eine überholte Version und wird abgewiesen — doppelt
 * geschrieben wird nie. Beschreibt der gespeicherte Stand den Versuch bereits
 * (`konfliktBereitsErfuellt`), ist das „bereits gespeichert“, kein Konflikt.
 * Für unversionierte Schreibzugriffe ist dieser Baustein nicht gedacht.
 *
 * TAB-GEBUNDEN (Entscheidung E4 im Protokoll AC-409): Gespeichert wird über
 * den `Entwurfsspeicher` (`sessionStorage`, an die Person gebunden, höchstens
 * eine Stunde). Automatisch abgeschickt wird nur, was in DIESER Seite
 * eingegeben wurde. Nach einem Neuladen ist der Wert nur noch `geborgen` und
 * wird angeboten, nie selbsttätig gesendet (`apps/web/CLAUDE.md`, „Ein
 * geborgener Entwurf wird nie automatisch abgeschickt“).
 */

/** Was aus einem Auftrag geworden ist; `melde` bekommt jeden Ausgang. */
export type Ausgang<T> =
  /** Der Server hat geschrieben. */
  | { readonly art: 'gespeichert'; readonly ergebnis: T }
  /** Ein früherer Versuch hat gewirkt, oder jemand hat dasselbe eingetragen. */
  | { readonly art: 'bereits_gespeichert'; readonly konflikt: OptimisticLockConflict }
  /** Die Version ist überholt: beide Werte, der Mensch entscheidet (AK 2). */
  | { readonly art: 'konflikt'; readonly konflikt: OptimisticLockConflict }
  /** Keine Verbindung: der Wert wartet auf die Wiederverbindung. */
  | { readonly art: 'wartet' }
  /** Jeder andere Befund; der Wert bleibt bis `verwerfen` geborgen. */
  | { readonly art: 'fehler'; readonly befund: FehlerBefund }
  /**
   * Ein neuerer Wert desselben Schlüssels oder `verwerfen` kam dazwischen; es
   * zählt dessen Ausgang. Wird nicht gemeldet, nur von `absenden` geliefert.
   *
   * ACHTUNG FÜR DEN AUFRUFER (AC-403): Jeder Wert trägt die Version, die der
   * Aufrufer ihm mitgibt. Wird B abgesendet, während A noch läuft, und A wird
   * gespeichert, trifft B die überholte Version und kommt als `konflikt` gegen
   * A zurück — nie still überschrieben, aber gegen die eigene Änderung. Die
   * Zelle nimmt deshalb die Version aus der letzten Antwort und schickt erst
   * danach weiter, oder sie sperrt die Eingabe, solange gesendet wird.
   */
  | { readonly art: 'ueberholt' };

/** Woran die Warteschlange merkt, dass die Verbindung wieder steht. */
export interface Verbindung {
  /** Meldet jede Wiederverbindung; liefert die Abmeldung. */
  beiWiederverbindung(this: void, rueckruf: () => void): () => void;
}

/** Das `online`-Ereignis des Browsers; ohne `window` (Tests) meldet es nie. */
export const browserVerbindung: Verbindung = {
  beiWiederverbindung(rueckruf) {
    if (typeof window === 'undefined') {
      return () => undefined;
    }

    window.addEventListener('online', rueckruf);

    return () => {
      window.removeEventListener('online', rueckruf);
    };
  },
};

export interface SchreibwarteschlangeOptionen<N extends Versioned, T> {
  /** Name der Prozedur, zugleich Namensraum im Speicher, z. B. `allocations.updatePlanHours`. */
  readonly name: string;
  /** Der eigentliche Aufruf, z. B. `trpc.….mutate`. */
  readonly senden: (nutzlast: N) => Promise<T>;
  /** Prüfer für geborgene Werte: Text aus dem Browserspeicher ist nicht vertrauenswürdig. */
  readonly pruefe: (roh: unknown) => N | null;
  /**
   * Jeder Ausgang, auch der einer Wiederholung nach Wiederverbindung. Darf
   * nicht werfen.
   */
  readonly melde: (schluessel: string, ausgang: Ausgang<T>) => void;
  /** An die angemeldete Person gebunden (`erzeugeEntwurfsspeicher`). */
  readonly entwuerfe: Entwurfsspeicher;
  readonly verbindung?: Verbindung;
  /** Vorgabe `geteilterSerialisierer`; eine eigene Instanz nur in Tests. */
  readonly serialisierer?: Serialisierer;
}

export interface Schreibwarteschlange<N extends Versioned, T> {
  /**
   * Merkt den Wert und schickt ihn ab. `schluessel` ist der der Entität
   * (`entitaetsSchluessel`); ein neuer Wert für denselben Schlüssel ersetzt
   * einen wartenden oder laufenden (`ueberholt`).
   */
  absenden(this: void, schluessel: string, nutzlast: N): Promise<Ausgang<T>>;
  /** Ein Wert aus einer früheren Seite (Neuladen) — nur zum Anbieten. */
  geborgen(this: void, schluessel: string): N | null;
  /** Schlüssel, die in dieser Seite auf die Wiederverbindung warten. */
  wartend(this: void): readonly string[];
  /** Der Mensch hat entschieden: Wert weg, auch aus dem Speicher. */
  verwerfen(this: void, schluessel: string): void;
  /** Meldet sich von der Verbindung ab. */
  beenden(this: void): void;
}

export function erzeugeSchreibwarteschlange<N extends Versioned, T>(
  optionen: SchreibwarteschlangeOptionen<N, T>,
): Schreibwarteschlange<N, T> {
  const { name, senden, pruefe, melde, entwuerfe } = optionen;
  const serialisierer = optionen.serialisierer ?? geteilterSerialisierer;
  const verbindung = optionen.verbindung ?? browserVerbindung;
  /**
   * Der neueste Wert je Schlüssel, den der Mensch in DIESER Seite abgeschickt
   * und weder gespeichert bekommen noch verworfen hat. Nur er zählt: der
   * Ausgang eines überholten Werts ändert keinen Zustand und wird nicht
   * gemeldet. Nur was hier steht, wird selbsttätig wiederholt.
   */
  const neuester = new Map<string, N>();
  /** Schlüssel, deren neuester Wert am Netz gescheitert ist. */
  const wartet = new Set<string>();

  const speicherSchluessel = (schluessel: string): string => `warteschlange:${name}:${schluessel}`;

  async function sende(nutzlast: N): Promise<Ausgang<T>> {
    try {
      return { art: 'gespeichert', ergebnis: await senden(nutzlast) };
    } catch (error: unknown) {
      const befund = klassifiziereFehler(error, { schreibzugriff: true });

      if (befund.art === 'netz') {
        return { art: 'wartet' };
      }

      const konflikt = trpcKonflikt(error);

      if (konflikt !== null) {
        return konfliktBereitsErfuellt(konflikt)
          ? { art: 'bereits_gespeichert', konflikt }
          : { art: 'konflikt', konflikt };
      }

      return { art: 'fehler', befund };
    }
  }

  /** Sendet und meldet; `null`, wenn der Wert inzwischen überholt oder verworfen ist. */
  async function versuche(schluessel: string, nutzlast: N): Promise<Ausgang<T> | null> {
    const ausgang = await serialisierer.reihe(schluessel, () => sende(nutzlast));

    if (neuester.get(schluessel) !== nutzlast) {
      return null;
    }

    if (ausgang.art === 'wartet') {
      wartet.add(schluessel);
    } else {
      wartet.delete(schluessel);
    }

    // Nur nach dem Speichern weg. Bei Konflikt und Fehler bleibt der Wert
    // geborgen, bis der Mensch entschieden hat (`verwerfen`).
    if (ausgang.art === 'gespeichert' || ausgang.art === 'bereits_gespeichert') {
      neuester.delete(schluessel);
      entwuerfe.vergessen(speicherSchluessel(schluessel));
    }

    melde(schluessel, ausgang);

    return ausgang;
  }

  const abmelden = verbindung.beiWiederverbindung(() => {
    for (const schluessel of [...wartet]) {
      const nutzlast = neuester.get(schluessel);

      // Vor dem Senden austragen: ein zweites `online` schickt nicht doppelt.
      wartet.delete(schluessel);

      if (nutzlast !== undefined) {
        // `melde` darf nicht werfen; tut es doch, bleibt es bei diesem
        // Schlüssel und wird keine unbehandelte Ablehnung.
        void versuche(schluessel, nutzlast).catch(() => undefined);
      }
    }
  });

  return {
    async absenden(schluessel, nutzlast) {
      // ZUERST merken: geht die Seite zwischen Absenden und Antwort verloren,
      // ist der Wert danach geborgen statt weg.
      entwuerfe.merken(speicherSchluessel(schluessel), nutzlast);
      neuester.set(schluessel, nutzlast);
      wartet.delete(schluessel);

      return (await versuche(schluessel, nutzlast)) ?? { art: 'ueberholt' };
    },
    geborgen(schluessel) {
      return entwuerfe.holen(speicherSchluessel(schluessel), pruefe)?.eingabe ?? null;
    },
    wartend() {
      return [...wartet];
    },
    verwerfen(schluessel) {
      neuester.delete(schluessel);
      wartet.delete(schluessel);
      entwuerfe.vergessen(speicherSchluessel(schluessel));
    },
    beenden() {
      abmelden();
    },
  };
}
